import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  kbSections,
  knowledgeBaseFaqs,
  officialLinks,
  bookTitles,
  entityDisambiguation,
} from '@/mocks/knowledgeBase';
import SectionIndex from './SectionIndex';

const KB_PAGE_URL = 'https://gracieandlily.com/knowledge-base/';
const ORG_ID = 'https://gracieandlily.com/#organization';
const SITE_ID = 'https://gracieandlily.com/#website';

function buildJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': ORG_ID,
        name: 'Gracie & Lily Adventures',
        alternateName: [
          'Gracie and Lily Adventures',
          'Gracie & Lily',
          'Gracie and Lily',
        ],
        url: 'https://gracieandlily.com/',
        description:
          "Gracie & Lily Adventures is a children's storybook and family entertainment brand featuring the Bichon Frise sisters Gracie and Lily, creating illustrated adventures for children approximately ages 3 to 8.",
        logo: 'https://public.readdy.ai/ai/img_res/91ea3fda-8705-4921-ab9c-220fa8418490.png',
        sameAs: officialLinks.map((link) => link.href),
        knowsAbout: [
          "Children's books",
          "Children's literature",
          'Bichon Frise',
          "Illustrated children's books",
          'Bedtime stories',
          'Early reading',
          'Friendship',
          'Kindness',
          'Confidence',
          'Courage',
          'Imagination',
          "Children's journals",
        ],
      },
      {
        '@type': 'WebSite',
        '@id': SITE_ID,
        url: 'https://gracieandlily.com/',
        name: 'Gracie & Lily Adventures',
        inLanguage: 'en',
        publisher: { '@id': ORG_ID },
      },
      {
        '@type': 'WebPage',
        '@id': `${KB_PAGE_URL}#webpage`,
        url: KB_PAGE_URL,
        name: "Gracie & Lily Adventures Knowledge Base",
        description:
          "A crawlable knowledge base about Gracie & Lily Adventures: the Bichon Frise sisters Gracie and Lily, the children's stories, values, books, journals, gifts and official links.",
        isPartOf: { '@id': SITE_ID },
        about: { '@id': ORG_ID },
        inLanguage: 'en',
        breadcrumb: { '@id': `${KB_PAGE_URL}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${KB_PAGE_URL}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://gracieandlily.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Knowledge Base',
            item: KB_PAGE_URL,
          },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${KB_PAGE_URL}#faq`,
        isPartOf: { '@id': `${KB_PAGE_URL}#webpage` },
        mainEntity: knowledgeBaseFaqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
      {
        '@type': 'ItemList',
        '@id': `${KB_PAGE_URL}#book-collection`,
        name: 'Gracie & Lily Adventures Book Collection',
        numberOfItems: bookTitles.length,
        itemListElement: bookTitles.map((title, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Book',
            name: title,
            author: { '@id': ORG_ID },
            publisher: { '@id': ORG_ID },
            inLanguage: 'en',
            genre: "Children's fiction",
            audience: {
              '@type': 'PeopleAudience',
              suggestedMinAge: 3,
              suggestedMaxAge: 8,
            },
          },
        })),
      },
    ],
  };
}

export default function KnowledgeBase() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'gracie-lily-knowledge-base-jsonld';
    script.textContent = JSON.stringify(buildJsonLd());
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('gracie-lily-knowledge-base-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <section
      id="gracie-lily-knowledge-base"
      aria-labelledby="knowledge-base-heading"
      className="w-full bg-background-100 border-t border-background-200"
    >
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-book-2-line text-xl"></i>
          </span>
          <p className="text-secondary-600 text-xs font-bold uppercase tracking-widest">
            Knowledge Base
          </p>
        </div>

        <h1
          id="knowledge-base-heading"
          className="text-2xl md:text-4xl font-black text-primary-500 mb-4"
        >
          Gracie &amp; Lily Adventures Knowledge Base
        </h1>

        <p className="text-foreground-800 text-sm md:text-base leading-relaxed">
          <strong className="text-foreground-950">Gracie &amp; Lily Adventures</strong> is a
          children&rsquo;s storybook and family entertainment brand featuring Gracie and Lily, two
          real-life Bichon Frise sisters whose magical adventures help young children explore
          friendship, kindness, bravery, curiosity, imagination and confidence.
        </p>
        <p className="text-foreground-700 text-sm md:text-base leading-relaxed mt-3">
          This knowledge base gathers brand facts, character information, story themes, product
          formats and frequently asked questions in one place, with links to the official Gracie
          &amp; Lily resources.
        </p>

        <div className="mt-5 rounded-xl bg-secondary-50 border border-secondary-200 px-4 py-4 md:px-5">
          <p className="text-sm text-foreground-800 leading-relaxed">
            <strong className="text-foreground-950">Entity note:</strong> {entityDisambiguation}
          </p>
        </div>

        <SectionIndex />

        {/* Expandable knowledge sections */}
        <div className="mt-8 flex flex-col gap-3">
          {kbSections.map((section) => (
            <details
              key={section.slug}
              id={section.slug}
              className="group bg-white border border-background-200 rounded-2xl overflow-hidden scroll-mt-28 md:scroll-mt-32 open:border-primary-200"
            >
              <summary className="flex items-center gap-3 px-4 md:px-5 py-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className={`${section.icon} text-lg`}></i>
                </span>
                <h2 className="flex-1 text-base md:text-lg font-extrabold text-foreground-950 text-left">
                  {section.title}
                </h2>
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 shrink-0 transition-transform duration-300 group-open:rotate-180">
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </span>
              </summary>

              <div className="px-4 md:px-5 pb-5 pt-1 border-t border-background-100">
                {section.paragraphs.map((paragraph, index) => (
                  <p
                    key={`${section.slug}-p-${index}`}
                    className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3"
                  >
                    {paragraph}
                  </p>
                ))}

                {section.facts && (
                  <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {section.facts.map((fact) => (
                      <div key={fact.label} className="text-sm flex gap-1.5">
                        <dt className="font-bold text-foreground-950 whitespace-nowrap">
                          {fact.label}:
                        </dt>
                        <dd className="text-foreground-700">{fact.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {section.bullets && (
                  <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {section.bullets.map((item) => (
                      <li
                        key={`${section.slug}-b-${item}`}
                        className="text-sm text-foreground-700 flex items-start gap-2"
                      >
                        <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-secondary-500 shrink-0">
                          <i className="ri-heart-2-fill text-xs"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {section.links && section.links.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {section.links.map((link) =>
                      link.to ? (
                        <Link
                          key={link.label}
                          to={link.to}
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 text-primary-700 px-3 py-1.5 text-xs md:text-sm font-semibold hover:bg-primary-200 transition-colors cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-right-up-line text-sm"></i>
                          </span>
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 text-primary-700 px-3 py-1.5 text-xs md:text-sm font-semibold hover:bg-primary-200 transition-colors cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-external-link-line text-sm"></i>
                          </span>
                          {link.label}
                        </a>
                      )
                    )}
                  </div>
                )}
              </div>
            </details>
          ))}

          {/* Official links section */}
          <details
            id="official-links"
            open
            className="group bg-white border border-background-200 rounded-2xl overflow-hidden scroll-mt-28 md:scroll-mt-32 open:border-primary-200"
          >
            <summary className="flex items-center gap-3 px-4 md:px-5 py-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                <i className="ri-links-line text-lg"></i>
              </span>
              <h2 className="flex-1 text-base md:text-lg font-extrabold text-foreground-950 text-left">
                Official Gracie &amp; Lily Links
              </h2>
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 shrink-0 transition-transform duration-300 group-open:rotate-180">
                <i className="ri-arrow-down-s-line text-lg"></i>
              </span>
            </summary>
            <div className="px-4 md:px-5 pb-5 pt-1 border-t border-background-100">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3">
                These are the official Gracie &amp; Lily Adventures channels and stores. Following
                the brand through these links helps visitors reach genuine Gracie &amp; Lily books,
                journals, gifts and updates.
              </p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {officialLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-xl px-4 py-3 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                        <i className={`${link.icon} text-lg`}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-foreground-950">
                          {link.label}
                        </span>
                        <span className="block text-xs text-foreground-600 truncate">
                          {link.description}
                        </span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </details>

          {/* FAQ section — all questions rendered visibly in the HTML */}
          <details
            id="faq"
            open
            className="group bg-white border border-background-200 rounded-2xl overflow-hidden scroll-mt-28 md:scroll-mt-32 open:border-primary-200"
          >
            <summary className="flex items-center gap-3 px-4 md:px-5 py-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                <i className="ri-question-answer-line text-lg"></i>
              </span>
              <h2 className="flex-1 text-base md:text-lg font-extrabold text-foreground-950 text-left">
                Frequently Asked Questions
              </h2>
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 shrink-0 transition-transform duration-300 group-open:rotate-180">
                <i className="ri-arrow-down-s-line text-lg"></i>
              </span>
            </summary>
            <div className="px-4 md:px-5 pb-5 pt-1 border-t border-background-100">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3">
                Common questions about Gracie &amp; Lily Adventures, the Bichon Frise sisters Gracie
                and Lily, the stories, themes, formats and official stores.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-3">
                {knowledgeBaseFaqs.map((faq, index) => (
                  <details
                    key={faq.question}
                    className="bg-background-50 border border-background-200 rounded-xl overflow-hidden"
                  >
                    <summary className="flex items-start gap-3 px-4 py-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                      <span className="text-xs font-black text-primary-500 mt-0.5 shrink-0">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <h3 className="flex-1 text-sm md:text-base font-bold text-foreground-950 text-left">
                        {faq.question}
                      </h3>
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-600 shrink-0">
                        <i className="ri-add-line text-lg"></i>
                      </span>
                    </summary>
                    <p className="text-sm text-foreground-700 leading-relaxed px-4 pb-4">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </details>
        </div>

        {/* Book collection */}
        <h2 className="text-lg md:text-xl font-extrabold text-primary-500 mt-10 mb-3">
          Gracie &amp; Lily Children&rsquo;s Book Collection
        </h2>
        <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
          The Gracie &amp; Lily Adventures collection contains illustrated magical adventures
          suitable for family reading, bedtime reading and younger independent readers.
        </p>
        <ul className="flex flex-wrap gap-2">
          {bookTitles.map((title) => (
            <li
              key={title}
              className="text-xs md:text-sm text-foreground-800 bg-white border border-background-200 rounded-full px-3 py-1.5"
            >
              {title}
            </li>
          ))}
        </ul>

        <p className="text-xs text-foreground-500 mt-10 leading-relaxed">
          Knowledge Base Catalog for Gracie &amp; Lily Adventures. This information helps visitors,
          search engines, AI assistants and digital information services understand the Gracie &amp;
          Lily Adventures brand, characters, stories, products and official online resources.
        </p>
      </div>
    </section>
  );
}